import { get } from 'svelte/store';
import { getAddonStatus } from 'src/helpers/addons/addon_helpers';
import { mappedInputReaderEvent } from 'src/helpers/input_reader_helpers';
import { isEMWithoutSwitch, isProThreeEM } from 'src/helpers/interface_helpers';
import { getDeviceStatusNameTrans } from 'src/helpers/name_translations_helper';
import { capFirstChar } from 'src/helpers/utilities';
import { inputStates } from 'src/stores/features/input-states';
import type { LabelProps } from '@Shelly/shelly-design-system';
import { requestEditSharedDevice } from 'src/components/Lists/list-helpers';
import { bulkUpdate } from '../ApplicationSettings/app_settings_helper';
import { isXT1Module } from 'src/helpers/xt1_helpers/template_helpers';
import type { Device } from 'src/types/Device';
import { DECIMAL_PRECISION_MIN } from 'src/helpers/virtual_helpers/virtual_components_status_helpers';
import { VIRTUAL_DEVICE_CATEGORY } from 'src/helpers/virtual_helpers/virtual_components_helpers';

export type FormattedItemType = {
	n: string;
	o: number;
	m?: boolean;
	v: boolean;
};

export const getHasNoAction = (device: Device, statuses): boolean => {
	if (isProThreeEM(device.type)) {
		return getAddonStatus(device).find((i) => i.peripheralType == 'switch') == null;
	} else if (isXT1Module(device.type)) {
		return statuses.find((i) => i.hasAction) == null;
	} else if (device.category == VIRTUAL_DEVICE_CATEGORY) {
		return (
			statuses.find(
				(i) => i.vcType == 'button' || (i.vcType == 'boolean' && i.vcView == 'toggle'),
			) == null
		);
	}
	// module in 'inputs_reader' and 'shell' mode doesn't have control components
	else if (device.category == 'module' && /^(inputs_reader|shell)$/.test(device.mode)) {
		return true;
	}
	// Adding this, since EM devices without switch don't have control components
	else if (isEMWithoutSwitch(device.type)) {
		return true;
	}
	return /^(power_meter|inputs_reader|sensor|gateway|ir_controller)$/.test(device.category);
};

export const normalizeParams = (params) => {
	if (!params) {
		return;
	} else if (typeof params === 'object') {
		return Object.values(params);
	} else {
		return params;
	}
};

export const getSelectOptions = (statuses, isInputsReader, device, inputStates) => {
	let options = [];
	options = statuses
		.filter((i) => !i.hideLabel)
		.map((item) => {
			if (checkIfHasValue(item.value)) {
				return {
					...item,
					title: capFirstChar(`${getDeviceStatusNameTrans(item.name, item)}: ${item.value}`),
				};
			} else {
				return {
					...item,
					title: capFirstChar(getDeviceStatusNameTrans(item.name, item)),
				};
			}
		});

	if (isInputsReader) {
		options = [
			...options,
			{
				name: 'click combinations',
				title: capFirstChar(`${getDeviceStatusNameTrans('click combinations')}`),
				value:
					mappedInputReaderEvent[inputStates?.[device.id]?.event] !== undefined ? '' : 'N/A',
				icon: 'fas fa-hand-back-point-up',
			},
		];
	}

	return options;
};

export const existsInLabelList = (item, labelList) => {
	return labelList.some((el) => el.name === item.name);
};

export const setCorrectOrderForParams = (visible, paramsArr) => {
	paramsArr = paramsArr.map((item, index) => {
		paramsArr[index] = {
			...item,
			data: {
				...item.data,
				order: index + 1,
				visible: visible,
			},
		};
	});
};

export const getLabelListFromDndItems = (dndItems) => {
	return dndItems.map((item) => item.data).filter((i) => !i.hideLabel);
};

export const formatDataForDB = (items) => {
	const formattedData = items.map((item) => {
		const formattedItem: FormattedItemType = {
			n: item.name,
			o: item.order,
			v: item.visible,
			m: item?.main ? item.main : false,
		};

		return formattedItem;
	});

	return formattedData;
};

export const checkIfHasValue = (value) => {
	if (value || value === 0) {
		return String(value);
	} else {
		return '';
	}
};

export const getCustomizeLabelList = (device: Device, statuses): LabelProps[] => {
	let labelList = [];
	const statusLabelParamArr = normalizeParams(device?.statusLabelParam);
	const uniqueStatusLabelParamArr = Array.isArray(statusLabelParamArr)
		? statusLabelParamArr.filter(
				(item, index, self) =>
					item?.n && self.findIndex((candidate) => candidate?.n === item?.n) === index,
			)
		: statusLabelParamArr;

	if (uniqueStatusLabelParamArr?.length > 0) {
		const statusesToAdd = statuses.filter(
			(item2) =>
				!item2.hideLabel &&
				!item2.advLabel &&
				!uniqueStatusLabelParamArr.some((item1) => item1.n === item2.name),
		);

		labelList = uniqueStatusLabelParamArr
			.filter((item) => item?.v)
			.map((config) => {
				const foundItem = statuses.find((item) => item.name === config?.n);
				if (foundItem) {
					return {
						...foundItem,
						main: config?.m,
						order: config?.o,
					};
				} else if (config?.n === 'click combinations') {
					return {
						icon: 'fas fa-hand-back-point-up',
						name: config.n,
						main: config.m,
						dotsType: mappedInputReaderEvent[get(inputStates)?.[device.id]?.event],
					};
				} else {
					return {};
				}
			})
			.filter((item) => item?.name);

		if (statusesToAdd.length > 0) {
			statusesToAdd.forEach((element, i) => {
				labelList.push({
					...element,
					order: labelList.length + 1,
				});
			});
		}
	} else {
		labelList = statuses.filter(
			(label) =>
				'undefined' === typeof label.main &&
				!label.hideLabel &&
				!label.mainLabel &&
				!label.advLabel &&
				!/^(timer|schedule|last)$/.test(label.name) &&
				label.name !== undefined,
		);
	}

	return labelList;
};

export const resetCustomizeDeviceCard = async (device: Device) => {
	const deviceParams: {
		statusLabelParam: FormattedItemType[];
		mainActionType: string;
		virtualDecimalPrecision?: number;
	} = {
		mainActionType: null,
		statusLabelParam: null,
	};
	if (device?.category === VIRTUAL_DEVICE_CATEGORY) {
		deviceParams.virtualDecimalPrecision = DECIMAL_PRECISION_MIN;
	}

	if (device.shared) {
		await requestEditSharedDevice({ ...device, ...deviceParams });
	} else {
		await bulkUpdate(device.id, { ...device, ...deviceParams });
	}
};
